import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, Card, CardContent, CardActions,
  Chip, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Select, MenuItem, FormControl, InputLabel,
  FormControlLabel, Switch, CircularProgress, IconButton,
  Divider, Grid
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import supabase from '../../../supabaseClient';

const INTERVALS = [5, 10, 15, 30, 60];
const DIRECTIONS = [
  { value: 'up',      label: 'w górę' },
  { value: 'down',    label: 'w dół' },
  { value: 'nearest', label: 'do najbliższego' },
];

const EMPTY_FORM = {
  name: '',
  shift_start: '',
  shift_end: '',
  entry_tolerance_min: 5,
  exit_tolerance_min: 5,
  round_interval: 15,
  entry_round_dir: 'nearest',
  exit_round_dir: 'nearest',
  pay_early_entry: false,
  pay_late_exit: false,
  is_active: true,
};

function RuleCard({ rule, onEdit, onDelete }) {
  const dirLabel = v => DIRECTIONS.find(d => d.value === v)?.label ?? v;

  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardContent sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, flexGrow: 1 }}>
            {rule.name}
          </Typography>
          <Chip
            label={rule.is_active ? 'Aktywna' : 'Nieaktywna'}
            color={rule.is_active ? 'success' : 'default'}
            size="small"
          />
        </Box>

        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
          Zmiana: <strong>{rule.shift_start ?? '—'} → {rule.shift_end ?? '—'}</strong>
        </Typography>

        <Grid container spacing={2} sx={{ mt: 0 }}>
          <Grid item xs={6}>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Tolerancja wejścia: <strong>{rule.entry_tolerance_min} min</strong>
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Tolerancja wyjścia: <strong>{rule.exit_tolerance_min} min</strong>
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Interwał: <strong>{rule.round_interval} min</strong>
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Zaokr. wejścia: <strong>{dirLabel(rule.entry_round_dir)}</strong>
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Zaokr. wyjścia: <strong>{dirLabel(rule.exit_round_dir)}</strong>
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {rule.pay_early_entry && <Chip label="Płać za wcześniejsze przyjście" size="small" variant="outlined" />}
              {rule.pay_late_exit   && <Chip label="Płać za zostanie po czasie" size="small" variant="outlined" />}
            </Box>
          </Grid>
        </Grid>
      </CardContent>

      <CardActions sx={{ justifyContent: 'flex-end', pt: 0 }}>
        <IconButton size="small" onClick={() => onEdit(rule)}>
          <EditIcon fontSize="small" />
        </IconButton>
        <IconButton size="small" onClick={() => onDelete(rule)}
          sx={{ '&:hover': { color: 'error.main' } }}>
          <DeleteIcon fontSize="small" />
        </IconButton>
      </CardActions>
    </Card>
  );
}

function RuleFormDialog({ open, onClose, onSaved, idConfig, initial }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm(initial ? { ...EMPTY_FORM, ...initial } : { ...EMPTY_FORM });
  }, [initial, open]);

  const set = (field, value) => setForm(f => ({ ...f, [field]: value }));

  const validate = () => {
    if (!form.name.trim()) { alert('Podaj nazwę reguły.'); return false; }
    if (!form.round_interval) { alert('Wybierz interwał zaokrąglania.'); return false; }
    return true;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);

    const payload = {
      ...form,
      id_configuration: idConfig,
      entry_tolerance_min: Number(form.entry_tolerance_min),
      exit_tolerance_min:  Number(form.exit_tolerance_min),
      round_interval:      Number(form.round_interval),
      shift_start: form.shift_start || null,
      shift_end:   form.shift_end   || null,
    };
    delete payload.id;
    delete payload.created_at;

    const { error } = initial?.id
      ? await supabase.from('rounding_rules').update(payload).eq('id', initial.id)
      : await supabase.from('rounding_rules').insert(payload);

    setSaving(false);
    if (error) { console.log(error); alert('Błąd zapisu: ' + error.message); return; }
    onSaved();
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{initial?.id ? 'Edytuj regułę' : 'Dodaj regułę'}</DialogTitle>
      <DialogContent>
        <TextField label="Nazwa reguły" fullWidth value={form.name}
          onChange={e => set('name', e.target.value)} sx={{ mt: 2 }} />

        <Divider sx={{ my: 2 }}>Godziny zmiany</Divider>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField label="Początek zmiany" type="time" fullWidth value={form.shift_start}
            onChange={e => set('shift_start', e.target.value)}
            onClick={e => e.target.showPicker?.()}
            InputLabelProps={{ shrink: true }}
            sx={{ '& input:not([value=""])::-webkit-datetime-edit': { opacity: 1 }, '& input[value=""]::-webkit-datetime-edit': { opacity: 0 } }} />
          <TextField label="Koniec zmiany" type="time" fullWidth value={form.shift_end}
            onChange={e => set('shift_end', e.target.value)}
            onClick={e => e.target.showPicker?.()}
            InputLabelProps={{ shrink: true }}
            sx={{ '& input:not([value=""])::-webkit-datetime-edit': { opacity: 1 }, '& input[value=""]::-webkit-datetime-edit': { opacity: 0 } }} />
        </Box>

        <Divider sx={{ my: 2 }}>Tolerancje</Divider>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField label="Tolerancja wejścia (min)" type="number" fullWidth
            value={form.entry_tolerance_min}
            onChange={e => set('entry_tolerance_min', e.target.value)}
            inputProps={{ min: 0, max: 60 }} />
          <TextField label="Tolerancja wyjścia (min)" type="number" fullWidth
            value={form.exit_tolerance_min}
            onChange={e => set('exit_tolerance_min', e.target.value)}
            inputProps={{ min: 0, max: 60 }} />
        </Box>

        <Divider sx={{ my: 2 }}>Zaokrąglanie</Divider>
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Interwał zaokrąglania</InputLabel>
          <Select value={form.round_interval} label="Interwał zaokrąglania"
            onChange={e => set('round_interval', e.target.value)}>
            {INTERVALS.map(i => <MenuItem key={i} value={i}>{i} min</MenuItem>)}
          </Select>
        </FormControl>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <FormControl fullWidth>
            <InputLabel>Kierunek wejścia</InputLabel>
            <Select value={form.entry_round_dir} label="Kierunek wejścia"
              onChange={e => set('entry_round_dir', e.target.value)}>
              {DIRECTIONS.map(d => <MenuItem key={d.value} value={d.value}>{d.label}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>Kierunek wyjścia</InputLabel>
            <Select value={form.exit_round_dir} label="Kierunek wyjścia"
              onChange={e => set('exit_round_dir', e.target.value)}>
              {DIRECTIONS.map(d => <MenuItem key={d.value} value={d.value}>{d.label}</MenuItem>)}
            </Select>
          </FormControl>
        </Box>

        <Divider sx={{ my: 2 }}>Dodatkowe opcje</Divider>
        <FormControlLabel control={<Switch checked={form.pay_early_entry}
          onChange={e => set('pay_early_entry', e.target.checked)} />}
          label="Płać za wcześniejsze przyjście" />
        <FormControlLabel control={<Switch checked={form.pay_late_exit}
          onChange={e => set('pay_late_exit', e.target.checked)} />}
          label="Płać za zostanie po czasie" sx={{ display: 'block' }} />
        <FormControlLabel control={<Switch checked={form.is_active}
          onChange={e => set('is_active', e.target.checked)} />}
          label="Reguła aktywna" sx={{ display: 'block', mt: 1 }} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Anuluj</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? <CircularProgress size={20} /> : 'Zapisz'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function RoundingRulesConfig({ idConfig }) {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchRules = async () => {
    if (!idConfig) return;
    setLoading(true);
    const { data, error } = await supabase
      .from('rounding_rules')
      .select('*')
      .eq('id_configuration', idConfig)
      .order('created_at', { ascending: true });
    setLoading(false);
    if (error) { console.log(error); return; }
    setRules(data || []);
  };

  useEffect(() => { fetchRules(); }, [idConfig]);

  const handleEdit = (rule) => { setEditTarget(rule); setFormOpen(true); };
  const handleAdd  = ()     => { setEditTarget(null); setFormOpen(true); };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    const { error } = await supabase.from('rounding_rules').delete().eq('id', deleteTarget.id);
    if (error) { console.log(error); alert('Błąd usuwania: ' + error.message); }
    setDeleteTarget(null);
    fetchRules();
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" sx={{ flexGrow: 1 }}>
          Reguły zaokrąglania czasu pracy
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleAdd}>
          Dodaj regułę
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : rules.length === 0 ? (
        <Typography sx={{ color: 'text.secondary' }}>
          Brak zdefiniowanych reguł. Kliknij „Dodaj regułę" aby utworzyć pierwszą.
        </Typography>
      ) : (
        rules.map(r => (
          <RuleCard key={r.id} rule={r}
            onEdit={handleEdit}
            onDelete={setDeleteTarget}
          />
        ))
      )}

      <RuleFormDialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSaved={fetchRules}
        idConfig={idConfig}
        initial={editTarget}
      />

      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs">
        <DialogTitle>Usuń regułę</DialogTitle>
        <DialogContent>
          <Typography>
            Czy na pewno chcesz usunąć regułę <strong>{deleteTarget?.name}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Anuluj</Button>
          <Button variant="contained" color="error" onClick={confirmDelete}>Usuń</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default RoundingRulesConfig;
